import React, { useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon, CalendarIcon, ClockIcon, CheckSquareIcon, MessageSquareIcon, ExternalLinkIcon, CalendarPlusIcon, ListTodoIcon, BellPlusIcon, BookOpenIcon, FileTextIcon, AlertCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLessons } from '../../src/hooks/useLessons';
import { getTeacherTasks, Task } from '../../services/taskService';
import { getStudentPracticeLogs, PracticeLog } from '../../services/practiceLogService';
interface StudentProgressPanelProps {
  open: boolean;
  student: {
    id: string;
    studentId: string;
    name: string;
    instrument: string;
    progressTrend: string;
    trendDirection: 'up' | 'down' | 'neutral';
    practiceLevel: string;
    weeklyPracticeHours: string;
    attendanceSummary: string;
    attendanceValue: number;
    taskCompletionRate: string;
  } | null;
  onClose: () => void;
  onViewFullProfile: () => void;
  onMessage: () => void;
  darkMode: boolean;
}
export const StudentProgressPanel: React.FC<StudentProgressPanelProps> = ({
  open,
  student,
  onClose,
  onViewFullProfile,
  onMessage,
  darkMode
}) => {
  const navigate = useNavigate();
  const {
    currentUser
  } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const [animation, setAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const lastActiveElement = useRef<HTMLElement | null>(null);
  // State for last lesson
  const [lastLesson, setLastLesson] = useState<any>(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  // State for upcoming lesson
  const [upcomingLesson, setUpcomingLesson] = useState<any>(null);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  // State for recent practice log
  const [recentPractice, setRecentPractice] = useState<PracticeLog | null>(null);
  const [loadingPractice, setLoadingPractice] = useState(false);
  // State for recent tasks
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  // Fetch lessons for this student (extended range for better data)
  const today = new Date();
  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  const threeMonthsAhead = new Date(today);
  threeMonthsAhead.setMonth(today.getMonth() + 3);
  const {
    lessons
  } = useLessons({
    teacherId: currentUser?.uid || '',
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    range: {
      fromISOUtc: sixMonthsAgo.toISOString(),
      toISOUtc: threeMonthsAhead.toISOString()
    }
  });
  // Load lesson data when student changes
  useEffect(() => {
    if (!student || !lessons.length) {
      setLastLesson(null);
      setUpcomingLesson(null);
      return;
    }
    setLoadingLesson(true);
    setLoadingUpcoming(true);
    try {
      const now = new Date();
      // Filter lessons for this student
      const studentLessons = lessons.filter(lesson => lesson.studentId === student.studentId || lesson.studentId === student.id);
      // Find the most recent COMPLETED lesson
      const completedLessons = studentLessons.filter(lesson => {
        const lessonDate = new Date(lesson.startAt);
        return lesson.status === 'COMPLETED' && lessonDate <= now;
      }).sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
      if (completedLessons.length > 0) {
        setLastLesson(completedLessons[0]);
      } else {
        setLastLesson(null);
      }
      // Find the next UPCOMING lesson (future, not completed/canceled)
      const upcomingLessons = studentLessons.filter(lesson => {
        const lessonDate = new Date(lesson.startAt);
        return lessonDate > now && lesson.status !== 'COMPLETED' && lesson.status !== 'CANCELED';
      }).sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
      if (upcomingLessons.length > 0) {
        setUpcomingLesson(upcomingLessons[0]);
      } else {
        setUpcomingLesson(null);
      }
    } catch (error) {
      console.error('Error loading lesson data:', error);
      setLastLesson(null);
      setUpcomingLesson(null);
    } finally {
      setLoadingLesson(false);
      setLoadingUpcoming(false);
    }
  }, [student, lessons]);
  // Load recent practice log when student changes
  useEffect(() => {
    const loadPracticeLog = async () => {
      if (!student || !currentUser?.uid) {
        setRecentPractice(null);
        return;
      }
      setLoadingPractice(true);
      try {
        // Fetch practice logs for last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const startDate = thirtyDaysAgo.toISOString().split('T')[0];
        const endDate = new Date().toISOString().split('T')[0];
        const practiceLogs = await getStudentPracticeLogs(currentUser.uid, student.studentId || student.id, startDate, endDate);
        // Get the most recent practice log
        if (practiceLogs.length > 0) {
          setRecentPractice(practiceLogs[0]);
        } else {
          setRecentPractice(null);
        }
      } catch (error) {
        console.error('Error loading practice log:', error);
        setRecentPractice(null);
      } finally {
        setLoadingPractice(false);
      }
    };
    loadPracticeLog();
  }, [student, currentUser]);
  // Load recent tasks when student changes
  useEffect(() => {
    const loadTasks = async () => {
      if (!student || !currentUser?.uid) {
        setRecentTasks([]);
        return;
      }
      setLoadingTasks(true);
      try {
        const allTasks = await getTeacherTasks(currentUser.uid);
        // Filter tasks for this student
        const studentTasks = allTasks.filter(task => task.studentId === student.studentId || task.studentId === student.id);
        // Sort by due date (most recent first) and take last 2
        const sortedTasks = studentTasks.sort((a, b) => {
          const dateA = new Date(a.dueDate || a.deadline || 0);
          const dateB = new Date(b.dueDate || b.deadline || 0);
          return dateB.getTime() - dateA.getTime();
        });
        setRecentTasks(sortedTasks.slice(0, 2));
      } catch (error) {
        console.error('Error loading tasks:', error);
        setRecentTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, [student, currentUser]);
  // Handle panel animation
  useEffect(() => {
    if (open) {
      setAnimation('entering');
      setTimeout(() => setAnimation('entered'), 10);
      lastActiveElement.current = document.activeElement as HTMLElement;
    } else {
      setAnimation('exiting');
      setTimeout(() => setAnimation('exited'), 300);
    }
  }, [open]);
  // Focus management
  useEffect(() => {
    if (animation === 'entered' && panelRef.current) {
      panelRef.current.focus();
    }
    if (animation === 'exited' && lastActiveElement.current) {
      lastActiveElement.current.focus();
    }
  }, [animation]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);
  // Don't render if not open
  if (animation === 'exited') return null;
  if (!student) return null;
  // Get progress trend styling
  const getProgressTrendClass = (trend: string) => {
    switch (trend) {
      case 'Improving':
        return darkMode ? 'bg-green-500/20 text-green-500 border border-green-500/30' : 'bg-green-500/10 text-green-500 border border-green-500/30';
      case 'Steady':
        return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'Needs Support':
        return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      default:
        return darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  // Get practice level color
  const getPracticeLevelColor = (level: string) => {
    switch (level) {
      case 'Excellent':
        return 'text-green-500';
      case 'Good':
        return 'text-blue-500';
      case 'Fair':
        return 'text-yellow-500';
      case 'Needs Attention':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };
  // Format date helper
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Format date with time helper
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  // Format duration helper
  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };
  // Truncate notes helper
  const truncateNotes = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };
  // Get task status badge
  const getTaskStatusBadge = (task: Task) => {
    const now = new Date().getTime();
    const dueDateStr = task.deadline || task.dueDate;
    const dueDate = dueDateStr ? new Date(dueDateStr) : null;
    const isOverdue = dueDate && dueDate.getTime() < now && task.status !== 'completed';
    if (task.status === 'completed') {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-9077">
          Completed
        </span>;
    }
    if (isOverdue) {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`} data-id="element-9078">
          Overdue
        </span>;
    }
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-9079">
        Pending
      </span>;
  };
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${animation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={onClose} aria-hidden="true" data-id="element-9080" />

      {/* Panel */}
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="progress-panel-title" tabIndex={-1} className={`fixed inset-y-0 right-0 w-full sm:w-[500px] ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${animation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} data-id="element-9081">
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 flex items-center justify-between border-b ${darkMode ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-9082">
          <div className="flex items-center gap-3" data-id="element-9083">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-9084">
              <UserIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-9085" />
            </div>
            <div data-id="element-9086">
              <h2 id="progress-panel-title" className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-9087">
                {student.name}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9088">
                {student.instrument}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`p-2 rounded-full ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-9089">
            <XIcon className="h-5 w-5" data-id="element-9090" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-9091">
          {/* Key Metrics */}
          <div data-id="element-9092">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-9093">
              Key Metrics
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-9094">
              {/* Progress Trend */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9095">
                <div className="flex items-center justify-between mb-2" data-id="element-9096">
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9097">
                    Progress Trend
                  </p>
                  {student.trendDirection === 'up' && <TrendingUpIcon className="h-4 w-4 text-green-500" data-id="element-9098" />}
                  {student.trendDirection === 'down' && <TrendingDownIcon className="h-4 w-4 text-red-500" data-id="element-9099" />}
                  {student.trendDirection === 'neutral' && <MinusIcon className="h-4 w-4 text-gray-400" data-id="element-9100" />}
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProgressTrendClass(student.progressTrend)}`} data-id="element-9101">
                  {student.progressTrend}
                </span>
              </div>

              {/* Practice Level */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9102">
                <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9103">
                  Practice Level
                </p>
                <p className={`font-semibold ${getPracticeLevelColor(student.practiceLevel)}`} data-id="element-9104">
                  {student.practiceLevel}
                </p>
                {student.weeklyPracticeHours !== '0.0' && <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9105">
                    {student.weeklyPracticeHours} hrs/week
                  </p>}
              </div>

              {/* Attendance */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9106">
                <div className="flex items-center gap-2 mb-2" data-id="element-9107">
                  <CalendarIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-9108" />
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9109">
                    Attendance
                  </p>
                </div>
                <p className={`text-2xl font-bold ${student.attendanceValue >= 90 ? 'text-green-500' : student.attendanceValue >= 75 ? 'text-blue-500' : 'text-red-500'}`} data-id="element-9110">
                  {student.attendanceSummary}
                </p>
              </div>

              {/* Task Completion */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9111">
                <div className="flex items-center gap-2 mb-2" data-id="element-9112">
                  <CheckSquareIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9113" />
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9114">
                    Tasks Complete
                  </p>
                </div>
                <p className={`text-2xl font-bold ${parseInt(student.taskCompletionRate) >= 80 ? 'text-green-500' : parseInt(student.taskCompletionRate) >= 60 ? 'text-blue-500' : 'text-red-500'}`} data-id="element-9115">
                  {student.taskCompletionRate}%
                </p>
              </div>
            </div>
          </div>

          {/* Recent Activity Section */}
          <div data-id="element-9116">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-9117">
              Recent Activity
            </h3>
            <div className="space-y-3" data-id="element-9118">
              {/* Last Lesson */}
              <div data-id="element-9119">
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9120">
                  Last Lesson
                </p>
                {loadingLesson ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9121">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9122">
                      Loading...
                    </p>
                  </div> : lastLesson ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9123">
                    <div className="flex items-start justify-between mb-2" data-id="element-9124">
                      <div className="flex-1" data-id="element-9125">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-9126">
                          {formatDate(lastLesson.startAt)}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9127">
                          {formatDuration(lastLesson.durationMinutes)}
                        </p>
                      </div>
                      <FileTextIcon className="h-5 w-5 text-[#3B82F6]" data-id="element-9128" />
                    </div>
                    {lastLesson.notes && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9129">
                        {truncateNotes(lastLesson.notes)}
                      </p>}
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9130">
                    <BookOpenIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-9131" />
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9132">
                      No recent lessons
                    </p>
                  </div>}
              </div>

              {/* Recent Practice */}
              <div data-id="element-9133">
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9134">
                  Recent Practice
                </p>
                {loadingPractice ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9135">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9136">
                      Loading...
                    </p>
                  </div> : recentPractice ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9137">
                    <div className="flex items-start justify-between mb-2" data-id="element-9138">
                      <div className="flex-1" data-id="element-9139">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-9140">
                          {recentPractice.piece}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9141">
                          {formatDuration(recentPractice.duration)} •{' '}
                          {formatDate(recentPractice.date)}
                        </p>
                      </div>
                      <ClockIcon className="h-5 w-5 text-[#3B82F6]" data-id="element-9142" />
                    </div>
                    {recentPractice.notes && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9143">
                        {truncateNotes(recentPractice.notes)}
                      </p>}
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9144">
                    <ClockIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-9145" />
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9146">
                      No recent practice logs
                    </p>
                  </div>}
              </div>

              {/* Upcoming Lesson */}
              <div data-id="element-9147">
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9148">
                  Upcoming Lesson
                </p>
                {loadingUpcoming ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9149">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9150">
                      Loading...
                    </p>
                  </div> : upcomingLesson ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9151">
                    <div className="flex items-start justify-between" data-id="element-9152">
                      <div className="flex-1" data-id="element-9153">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-9154">
                          {formatDateTime(upcomingLesson.startAt)}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9155">
                          {formatDuration(upcomingLesson.durationMinutes)}
                        </p>
                      </div>
                      <CalendarIcon className="h-5 w-5 text-[#3B82F6]" data-id="element-9156" />
                    </div>
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9157">
                    <CalendarIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-9158" />
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9159">
                      No upcoming lessons
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Recent Tasks */}
          <div data-id="element-9160">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-9161">
              Recent Tasks
            </h3>
            {loadingTasks ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9162">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9163">
                  Loading...
                </p>
              </div> : recentTasks.length > 0 ? <div className="space-y-3" data-id="element-9164">
                {recentTasks.map(task => <div key={task.id} className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`} data-id="element-9165">
                    <div className="flex items-start justify-between mb-2" data-id="element-9166">
                      <div className="flex-1 min-w-0" data-id="element-9167">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'} truncate`} data-id="element-9168">
                          {task.title}
                        </p>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9169">
                          Due: {formatDate(task.dueDate || task.deadline || '')}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0" data-id="element-9170">
                        {getTaskStatusBadge(task)}
                      </div>
                    </div>
                  </div>)}
              </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'} text-center`} data-id="element-9171">
                <CheckSquareIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-9172" />
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9173">
                  No recent tasks
                </p>
              </div>}
          </div>

          {/* Quick Actions */}
          <div data-id="element-9174">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-9175">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-9176">
              <button onClick={() => {
              navigate('/calendar');
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-[#0a0e1a] hover:bg-[#0f1420] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-9177">
                <CalendarPlusIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-9178" />
                <span className="text-sm font-medium" data-id="element-9179">Schedule</span>
              </button>

              <button onClick={() => {
              onMessage();
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-[#0a0e1a] hover:bg-[#0f1420] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-9180">
                <MessageSquareIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-9181" />
                <span className="text-sm font-medium" data-id="element-9182">Message</span>
              </button>

              <button onClick={() => {
              navigate('/teacher-tasks');
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-[#0a0e1a] hover:bg-[#0f1420] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-9183">
                <ListTodoIcon className="h-4 w-4" data-id="element-9184" />
                <span className="text-sm font-medium" data-id="element-9185">Add Task</span>
              </button>

              <button onClick={() => {
              // This would open the add alert modal with this student pre-selected
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-[#0a0e1a] hover:bg-[#0f1420] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-9186">
                <BellPlusIcon className="h-4 w-4" data-id="element-9187" />
                <span className="text-sm font-medium" data-id="element-9188">Alert</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer - View Full Profile */}
        <div className={`sticky bottom-0 p-6 border-t ${darkMode ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-9189">
          <button onClick={() => {
          onViewFullProfile();
          onClose();
        }} className="w-full px-4 py-3 rounded-lg border-2 border-[#10B981] text-[#10B981] bg-transparent hover:bg-[#10B981]/10 transition-colors flex items-center justify-center gap-2 font-medium" data-id="element-9190">
            <ExternalLinkIcon className="h-4 w-4" data-id="element-9191" />
            View Full Profile
          </button>
        </div>
      </div>
    </>;
};